import RiskBadge from './ui/RiskBadge';
import ResultKeywordTags from './investigation/ResultKeywordTags';
import MatchBadge from './investigation/MatchBadge';
import SourceAttribution from './investigation/SourceAttribution';
import ResultDomainFavicon from './investigation/ResultDomainFavicon';
import { FeedInspectButton, FeedInspectUrlLink } from './FeedInspectActions';
import { resolveInspectUrl } from '../utils/feedInspectUtils';
import { stripHtmlTags } from '../utils/personFeedDisplay';
import {
  FEED_CARD_SHELL,
  FEED_CARD_PRIMARY,
  FEED_CHIP,
  FEED_INSPECT_BTN,
  FEED_META,
  FEED_TITLE,
} from './investigation/feedCardStyles';

const typeStyles = {
  'Search Engine': {
    badge: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-200',
    chip: 'text-cyan-300',
  },
  'Social Profile': {
    badge: 'border-violet-500/30 bg-violet-500/10 text-violet-200',
    chip: 'text-violet-300',
  },
  'Verified Record': {
    badge: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200',
    chip: 'text-emerald-300',
  },
  'Legal Record': {
    badge: 'border-amber-500/30 bg-amber-500/10 text-amber-200',
    chip: 'text-amber-300',
  },
  'Business Index': {
    badge: 'border-blue-500/30 bg-blue-500/10 text-blue-200',
    chip: 'text-blue-300',
  },
};

const MENTION_CATEGORY_STYLE = {
  'MEDIA / PRESS': 'border-sky-500/40 bg-sky-500/10 text-sky-200',
  SOCIAL: 'border-violet-500/40 bg-violet-500/10 text-violet-200',
  REGISTRY: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
};

function trustPct(res) {
  const raw = Number(res?.source_reliability ?? res?.score);
  if (!Number.isFinite(raw) || raw <= 0) return null;
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/**
 * @param {{
 *   res: object,
 *   title: string,
 *   icon?: string,
 *   avatarUrl?: string | null,
 *   showPrimaryBadge?: boolean,
 *   mentionCategory?: 'MEDIA / PRESS'|'SOCIAL'|'REGISTRY'|null,
 *   showSnippet?: boolean,
 *   compactUrl?: boolean,
 *   contactEmails?: string[],
 *   contactPhones?: string[],
 * }} props
 */
export default function EvidenceCard({
  res,
  title,
  icon = '🔎',
  avatarUrl = null,
  showPrimaryBadge,
  mentionCategory = null,
  showSnippet = false,
  compactUrl = false,
  contactEmails = null,
  contactPhones = null,
}) {
  const sourceType = res?.source_type || 'Search Engine';
  // When parent controls the badge (Person feed), only the top-confidence row may show PRIMARY.
  const isPrimary =
    typeof showPrimaryBadge === 'boolean'
      ? showPrimaryBadge
      : res?.is_primary_identity === true;
  const reliability = res?.source_reliability ?? res?.score;
  const styles = typeStyles[sourceType] || typeStyles['Search Engine'];
  const trust = trustPct(res);
  const categoryLabel =
    mentionCategory && MENTION_CATEGORY_STYLE[mentionCategory] ? mentionCategory : null;

  const detailsObj = res?.details && typeof res.details === 'object' ? res.details : {};
  const inspectUrl = resolveInspectUrl(res);
  const showInspect = Boolean(inspectUrl);
  const safeTitle = (title || '').trim();
  const hasAnyBranding =
    Boolean(res?.source_display_name) ||
    Boolean(res?.source) ||
    Boolean(res?.platform) ||
    (Array.isArray(res?.sources) && res.sources.length > 0) ||
    Boolean(res?.source_type) ||
    (res?.source_reliability !== undefined && res?.source_reliability !== null);
  const showBrandingRow = hasAnyBranding;

  const emails = [
    ...(Array.isArray(contactEmails) ? contactEmails : []),
    res?.found_email ? String(res.found_email) : '',
  ]
    .map((e) => String(e || '').trim())
    .filter(Boolean)
    .filter((e, i, arr) => arr.findIndex((x) => x.toLowerCase() === e.toLowerCase()) === i)
    .slice(0, 4);
  const phones = [
    ...(Array.isArray(contactPhones) ? contactPhones : []),
    res?.found_phone ? String(res.found_phone) : '',
  ]
    .map((p) => String(p || '').trim())
    .filter(Boolean)
    .filter((p, i, arr) => arr.findIndex((x) => x === p) === i)
    .slice(0, 4);

  const ctLookupSource =
    typeof detailsObj.lookup_source === 'string' && detailsObj.lookup_source.trim()
      ? detailsObj.lookup_source.trim()
      : '';
  let activeServices = { ...(detailsObj.active_services_urls || {}) };
  const gaiaRaw = detailsObj.gaia_id != null ? String(detailsObj.gaia_id).trim() : '';
  if (res?.source === 'ghunt' && gaiaRaw && !activeServices.Maps) {
    activeServices = {
      ...activeServices,
      Maps: `https://www.google.com/maps/contrib/${gaiaRaw}/reviews`,
    };
  }
  const serviceEntries = Object.entries(activeServices).filter(
    ([, u]) => typeof u === 'string' && u.trim().startsWith('http'),
  );
  const actionCount = (showInspect ? 1 : 0) + serviceEntries.length;
  const stackActionsOnMobile = actionCount > 2;

  return (
    <article
      className={`${FEED_CARD_SHELL} ${isPrimary ? FEED_CARD_PRIMARY : ''}`.trim()}
      data-feed-result="true"
      {...(isPrimary ? { 'data-primary-result': 'true' } : {})}
    >
      {/* Desktop: source chip + Inspect in header. Mobile: Inspect moves under title. */}
      <header className="mb-2 hidden min-w-0 flex-wrap items-start justify-between gap-2 sm:mb-3 sm:flex">
        {showBrandingRow ? (
          <span
            className={`inline-flex max-w-[min(100%,14rem)] shrink items-center truncate rounded-lg border px-2 py-1 text-[10px] font-black uppercase tracking-wider sm:text-[11px] ${styles.badge}`}
            title="Source category"
          >
            {sourceType}
          </span>
        ) : (
          <span />
        )}
        {showInspect || res?.details || res?.snippet ? (
          <FeedInspectButton res={res} />
        ) : null}
      </header>

      <section className="flex min-w-0 flex-col items-stretch gap-2 sm:flex-row sm:items-start sm:gap-5">
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center self-start overflow-hidden rounded-lg border bg-slate-900/80 shadow-inner transition sm:mx-0 sm:h-14 sm:w-14 sm:rounded-xl ${
            isPrimary
              ? 'border-[#00E5FF]/55 shadow-[0_0_16px_rgba(0,229,255,0.25)]'
              : 'border-white/10 group-hover:border-cyan-500/20'
          }`}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <ResultDomainFavicon res={res} fallback={icon} className="h-5 w-5 sm:h-8 sm:w-8" />
          )}
        </span>

        <section className="min-w-0 flex-1 space-y-1.5 sm:space-y-2">
          {/* Mobile row 1: Primary Target + trust only */}
          {isPrimary ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex w-fit items-center rounded-md border border-[#00E5FF]/50 bg-[#00E5FF]/15 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-[#00E5FF] sm:text-[11px]">
                Primary Target
              </span>
              {trust != null ? (
                <span
                  className="inline-flex w-fit items-center rounded-md border border-[#00E5FF]/35 bg-slate-950/70 px-2 py-1 text-[10px] font-bold text-[#00E5FF] sm:text-[11px]"
                  title="Identity confidence"
                >
                  {trust >= 80 ? 'High Relevance' : `${trust}% Trust`}
                </span>
              ) : null}
            </div>
          ) : null}
          {!isPrimary && categoryLabel ? (
            <span
              className={`inline-flex w-fit items-center rounded-md border px-2 py-1 text-[10px] font-black uppercase tracking-wider sm:text-[11px] ${MENTION_CATEGORY_STYLE[categoryLabel]}`}
            >
              {categoryLabel}
            </span>
          ) : null}
          {res?.wiw_result_origin === 'location_append' ? (
            <span className="inline-flex w-fit max-w-full items-center rounded-md border border-violet-500/35 bg-violet-500/15 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-violet-100 sm:text-[11px]">
              Added · {String(res.wiw_location_context || 'location').slice(0, 48)}
              {String(res.wiw_location_context || '').length > 48 ? '…' : ''}
            </span>
          ) : null}

          {/* Source attribution — second row on mobile (sm+ stays with meta) */}
          {showBrandingRow ? (
            <p
              className={`flex flex-wrap items-center gap-2 ${FEED_META} ${
                isPrimary ? 'order-none' : ''
              }`}
            >
              <span className="inline-flex max-w-full items-center truncate rounded-lg border border-white/10 bg-slate-900/60 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-slate-200 sm:hidden">
                {sourceType}
              </span>
              <span className={`flex flex-wrap items-center gap-2 ${styles.chip}`}>
                <SourceAttribution res={res} />
                <RiskBadge isMention={!!res?.is_mention} />
              </span>
            </p>
          ) : null}

          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h3 className={`min-w-0 flex-1 ${FEED_TITLE}`}>{safeTitle || 'Record'}</h3>
            {!isPrimary ? <MatchBadge value={reliability} /> : null}
          </div>

          {/* Mobile: Inspect under title for touch target */}
          {(showInspect || res?.details || res?.snippet) ? (
            <div className="sm:hidden">
              <FeedInspectButton res={res} className="w-full" />
            </div>
          ) : null}

          {(emails.length > 0 || phones.length > 0) ? (
            <div
              className="flex flex-wrap items-center gap-1.5"
              data-testid="person-contact-badges"
              aria-label="Discovered contact leads"
            >
              {emails.map((email) => (
                <a
                  key={`email-${email}`}
                  href={`mailto:${email}`}
                  className="inline-flex max-w-full items-center truncate rounded-md border border-cyan-500/35 bg-cyan-500/10 px-2 py-1 text-[10px] font-semibold text-cyan-200 transition hover:bg-cyan-500/20"
                  title={`Email: ${email}`}
                >
                  ✉ {email}
                </a>
              ))}
              {phones.map((phone) => (
                <a
                  key={`phone-${phone}`}
                  href={`tel:${phone.replace(/\s+/g, '')}`}
                  className="inline-flex max-w-full items-center truncate rounded-md border border-emerald-500/35 bg-emerald-500/10 px-2 py-1 text-[10px] font-semibold text-emerald-200 transition hover:bg-emerald-500/20"
                  title={`Phone: ${phone}`}
                >
                  ☎ {phone}
                </a>
              ))}
            </div>
          ) : null}

          {showSnippet && stripHtmlTags(res?.snippet) ? (
            <p className="text-xs leading-relaxed text-slate-300 line-clamp-2 sm:line-clamp-3 sm:text-slate-400">
              {stripHtmlTags(res.snippet)}
            </p>
          ) : null}

          {typeof res?.ai_summary === 'string' && res.ai_summary.trim() ? (
            <p
              className="mt-0.5 line-clamp-2 text-xs italic leading-relaxed text-slate-400 sm:mt-1.5 sm:line-clamp-none"
              title={res.semantic_verified ? 'AI Synthesis (semantic match)' : 'AI Synthesis'}
            >
              <span className="mr-1.5 not-italic font-semibold uppercase tracking-wider text-slate-500">
                AI Synthesis
              </span>
              {res.ai_summary.trim()}
            </p>
          ) : null}

          <ResultKeywordTags res={res} max={2} className="max-sm:[&>li:nth-child(n+3)]:hidden" />

          {Array.isArray(res?.details?.cui_matches) && res.details.cui_matches.length > 0 ? (
            <ul className="flex flex-wrap gap-1">
              {res.details.cui_matches.slice(0, 3).map((cui) => (
                <li key={String(cui)} className={FEED_CHIP}>
                  CUI: {String(cui)}
                </li>
              ))}
            </ul>
          ) : null}

          {ctLookupSource ? (
            <span className={FEED_CHIP}>CT: {ctLookupSource}</span>
          ) : null}

          {showInspect ? (
            <FeedInspectUrlLink res={res} compactHost={compactUrl !== false} />
          ) : (
            <p className="font-mono text-[11px] text-slate-500">Digital record</p>
          )}

          {serviceEntries.length > 0 ? (
            <section
              className={`mt-3 border-t border-white/5 pt-3 ${
                stackActionsOnMobile
                  ? 'flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap'
                  : 'flex flex-wrap gap-2'
              }`}
            >
              <span className="w-full text-[9px] font-black uppercase tracking-widest text-slate-500">
                Google services (GHunt)
              </span>
              {serviceEntries.map(([service, url]) => (
                <a
                  key={service}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className={`${FEED_INSPECT_BTN} ${stackActionsOnMobile ? 'w-full sm:w-auto' : ''}`}
                >
                  Open {service}
                </a>
              ))}
            </section>
          ) : null}
        </section>
      </section>
    </article>
  );
}
